/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import {
	ColorfulTitle,
	CosmosButton,
	CosmosImage,
	DirectoryCardBadgeEl,
	DirectoryCardMetaGrid,
	DirectoryCardTagsSection,
} from "@cosmos/components";

import { css } from "@panda/css";
import { Box, HStack, VStack } from "@panda/jsx";

import type { CosmosButtonProps, DirectoryCardBadge, DirectoryCardMetaItem } from "@cosmos/components";
// `CosmosImage` re-exports next/image's props, but the compiled component type
// loses the `src`/`width`/... surface across the package boundary. Typing the
// props object as `ImageProps` here keeps them checked, then spreading into
// `CosmosImage` sidesteps the lossy boundary type.
import type { ImageProps } from "next/image";

const logoCss = css({ height: "auto", maxWidth: "300px", objectFit: "contain" });
const boxStyles = css({
	display: "flex",
	width: "300px",
	height: "300px",
	padding: "eds.dimension.64",
	borderRadius: "eds.dimension.64",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	border: "2px solid token(colors.eds.border.faint)",
	background: "eds.palette.transparent.light.005",
	overflow: "hidden",
	flexShrink: "0",
	backdropFilter: "blur(50px)",
});
const titleStyles = css({
	background: "var(--cosmos-gradient)",
	backgroundClip: "text",
	color: "transparent",
});
const badgeStyles = css({
	alignSelf: "center",
	borderRadius: "12.5px!",
	width: "44px!",
	height: "44px!",
	"&& svg": {
		width: "38px!",
		height: "38px!",
	},
});

const headerStyles = css({
	smDown: {
		flexWrap: "wrap",
	},
});

export interface PartnerProfileHeaderProps {
	name: string;
	logoUrl?: string;
	backHref: string;
	backLabel: string;
	badge: DirectoryCardBadge;
	meta: DirectoryCardMetaItem[];
	primaryTags: string[];
	secondaryTags: string[];
	buttons: CosmosButtonProps[];
}

/**
 * Profile header for the Service Partner detail page: the logo well alongside
 * the back link, tier badge + name, meta grid, industry/support tags, and the
 * website/email action buttons. Mirrors the grid card's badge/meta/tags surface
 * so the detail header reads consistently with the directory card.
 */
export function PartnerProfileHeader({
	name,
	logoUrl,
	backHref,
	backLabel,
	badge,
	meta,
	primaryTags,
	secondaryTags,
	buttons,
}: PartnerProfileHeaderProps): React.JSX.Element {
	const logoImageProps: ImageProps | null = logoUrl
		? { src: logoUrl, alt: "", width: 300, height: 300, className: logoCss }
		: null;

	return (
		<HStack gap="eds.dimension.32" className={headerStyles}>
			<Box className={boxStyles}>{logoImageProps ? <CosmosImage {...logoImageProps} /> : null}</Box>
			<VStack gap="eds.dimension.24" alignItems="start">
				<VStack gap="eds.dimension.12" alignItems="start">
					<CosmosButton variant="ghost" label={backLabel} href={backHref} icon="ArrowLeft" iconSize="sm" />
					<HStack gap="eds.dimension.16">
						<DirectoryCardBadgeEl badge={badge} className={badgeStyles} />
						<ColorfulTitle
							title={{ text: name, as: "h2", variant: "heading2XL", color: "eds.text.contrast" }}
							className={titleStyles}
						/>
					</HStack>
				</VStack>
				{meta.length > 0 ? <DirectoryCardMetaGrid items={meta} /> : null}
				<DirectoryCardTagsSection primaryTags={primaryTags} secondaryTags={secondaryTags} />
				{buttons.length > 0 ? (
					<HStack gap="eds.dimension.16">
						{buttons.map((button) => (
							<CosmosButton key={button.label} {...button} />
						))}
					</HStack>
				) : null}
			</VStack>
		</HStack>
	);
}
